import { useQueryClient } from '@tanstack/react-query';
import { Buffer } from 'buffer';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type { OsobyCertifikatDto } from '@gov-nx/api';
import {
	certificatesQuery,
	deleteCertificatesQuery,
	sendNewCertificatesQuery,
	usePoMutation,
	usePoQuery,
	shouldIgnoreError,
	isPoResponseError,
	getPoErrors,
	certificatesHistoryQuery,
	useAxiosInstance,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import {
	MB,
	useFileUpload,
	useProcessControl,
	useServiceAuthorization,
	useUserLoginInfo,
} from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '../definitions/codes';
import { FormInstanceNative, FormInstanceWeb } from './FormDefinitions';
import type {
	CertificatesContext,
	ProfileRobProcessControl,
} from './context.types';
import { getIgnoredError } from './utils';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const ProfilCertificatesContext = createContext<CertificatesContext | null>(
	null
);

interface ProfilCertificatesContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
	isWeb?: boolean;
}

const AUTHORIZATION_SERVICE_NAME = 'PODANI_CERTIFIKAT_ROB';

export function ProfilCertificatesContextProvider({
	children,
	code,
	isWeb = false,
}: ProfilCertificatesContextProviderProps) {
	const { setControls, controls } = useProcessControl<ProfileRobProcessControl>(
		{
			initialDataError: null,
			initialPhotoError: null,
			displayRemoveModal: false,
			displayCertificateAddingModal: false,
			selectedCertificate: undefined,
		}
	);
	const [certificates, setCertificates] = useState<OsobyCertifikatDto[]>([]);
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();
	const { loa } = useUserLoginInfo();
	const authContext = useAutorizaceContext();

	const hasPermission = !!(loa && ['HIGH', 'SUBSTANTIAL'].includes(loa));
	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);
	const formDefinition = isWeb
		? FormInstanceWeb({ code, isLowAuthorization })
		: FormInstanceNative({ code, isLowAuthorization });

	const historyQueryKey = ['profil-certificates', 'history'];
	const historyQuery = usePoQuery({
		queryKey: historyQueryKey,
		queryFn: async () => certificatesHistoryQuery(axiosInstance),
	});
	const query = usePoQuery({
		queryKey: ['profil-certificates'],
		queryFn: async () => certificatesQuery(axiosInstance),
		onError: (err) => {
			setControls({
				initialLoading: false,
				initialError: new GovError(err.message),
			});

			toastMessageError(err.message);
		},
	});

	const reloadView = () => {
		setControls({ initialLoading: true });

		query.refetch();
	};

	useEffect(() => {
		if (!query.isFetching) {
			setCertificates(query.data?.data?.seznam ?? []);
			setControls({ initialLoading: false });
		}
	}, [query.data, query.isFetching, setControls]);

	const submitMutation = usePoMutation(
		{
			mutationFn: sendNewCertificatesQuery(axiosInstance),
			onSuccess: async (data) => {
				setControls({ displayCertificateAddingModal: false });

				const nonHighAuthorizationFlow = async () => {
					formDefinition.formReset();
					toastMessageSuccess(tsn('certifikaty.pridano'));
					await queryClient.invalidateQueries({ queryKey: historyQueryKey });
					reloadView();
				};

				if (authContext) {
					authContext?.onInitAuthorization(
						AUTHORIZATION_SERVICE_NAME,
						data.headers['x-autorizaceid'],
						nonHighAuthorizationFlow
					);
				} else {
					await nonHighAuthorizationFlow();
				}
			},
			onError: (error) => {
				formDefinition.formReset();
				setControls({
					displayCertificateAddingModal: false,
					processError: error,
				});
				toastMessageError(error.message);
			},
		},
		{
			errorIgnoreFilter: shouldIgnoreError((error) => {
				const poErrors = isPoResponseError(error)
					? getPoErrors(error)
					: undefined;

				const ignoredError = getIgnoredError(poErrors);

				if (ignoredError) {
					toastMessageError(tsn('certifikaty.chyba.' + ignoredError.extKod));
					return true;
				}

				return false;
			}),
		}
	);

	const onSubmit = formDefinition.formMethods.handleSubmit((data) => {
		setControls({ processError: null });
		const file = data.nazevSouboru?.[0];

		if (!file) {
			return;
		}

		submitMutation.mutate(file);
	});

	const deleteMutation = usePoMutation({
		mutationFn: async (certificate: OsobyCertifikatDto) => {
			if (certificate.vydavatel && certificate.serioveCislo) {
				const publisherBase = Buffer.from(
					encodeURIComponent(certificate.vydavatel),
					'utf-8'
				).toString('base64');

				await deleteCertificatesQuery(axiosInstance, certificate.serioveCislo, {
					params: { vydavatel: publisherBase },
				});
			}
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: historyQueryKey });
			reloadView();
		},
	});

	const sortCertificates: CertificatesContext['sortCertificates'] = (
		field,
		sortOrder
	) => {
		const comparator = (a: OsobyCertifikatDto, b: OsobyCertifikatDto) => {
			if (field === 'vydavatel') {
				return sortOrder === 'asc'
					? (a?.vydavatel ?? '').localeCompare(b?.vydavatel ?? '')
					: (b?.vydavatel ?? '').localeCompare(a.vydavatel ?? '');
			} else if (field === 'serioveCislo') {
				const aValue = a.serioveCislo ? parseInt(a.serioveCislo, 10) : 0;
				const bValue = b.serioveCislo ? parseInt(b.serioveCislo, 10) : 0;
				return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
			} else if (field === 'platnostDo') {
				const dateA = a.platnostDo ? new Date(a.platnostDo) : null;
				const dateB = b.platnostDo ? new Date(b.platnostDo) : null;

				if (dateA && dateB) {
					return sortOrder === 'asc'
						? dateA.getTime() - dateB.getTime()
						: dateB.getTime() - dateA.getTime();
				} else if (dateA) {
					return -1;
				} else if (dateB) {
					return 1;
				}

				return 0;
			}
			return 0;
		};

		setCertificates([...certificates].sort(comparator));
	};

	const fileUploadHook = useFileUpload(
		formDefinition.formMethods,
		'nazevSouboru',
		{
			extensions: ['.cer', '.chain', '.crt', '.der', '.key', '.pem', '.txt'],
			maxFileSize: 10 * MB,
			maxAttachments: 1,
			maxSumFileSize: 10 * MB,
			multiple: true,
		}
	);

	return (
		<ProfilCertificatesContext.Provider
			value={{
				controls,
				setControls,
				certificates,
				deleteCertificate: deleteMutation.mutate,
				hasPermission,
				formDefinition,

				isSubmitting: submitMutation.isLoading,
				isAddButtonDisabled:
					fileUploadHook.hasNotFinishedUploads || submitMutation.isLoading,
				onSubmit,
				sortCertificates,
				fileUploadHook,
				history: historyQuery.data?.data.seznam ?? [],
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</ProfilCertificatesContext.Provider>
	);
}

export const useProfilCertificatesContextInstance = (): CertificatesContext =>
	useContext(ProfilCertificatesContext) as CertificatesContext;
